/**
 * Comercial Subscriptions Service — Governance (Sistema A em BRL)
 *
 * Endpoints founder-only (JWT super_admin via interceptor):
 *   GET    /api/governance/comercial/subscriptions
 *   GET    /api/governance/comercial/subscriptions/by-institution/{id}
 *   GET    /api/governance/comercial/subscriptions/preview-change?institution_id=X&plan_key=Y
 *   POST   /api/governance/comercial/subscriptions
 *   POST   /api/governance/comercial/subscriptions/{id}/cancel
 *
 * Disciplina: o service NORMALIZA o shape do backend pra um shape ergonômico
 * pra UI. Componentes ficam ingênuos — não conhecem os nomes do banco.
 */
import { AZURE } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de lookup em planos (para enriquecer family/size_band)
// ─────────────────────────────────────────────────────────────────────────────

const buildPlansMap = (plansArr) => {
  const m = {};
  (plansArr || []).forEach((p) => { m[p.plan_key] = p; });
  return m;
};

const _enrichSub = (subRaw, plansMap) => {
  // subRaw: {subscription_id, institution_id, institution_name, institution_type,
  //         plan_key, plan_name, status, athlete_limit, staff_limit,
  //         ai_credits_monthly, monthly_price_cents, athletes:{active,suspended_by_quota,limit_now}, ...}
  const planDef = plansMap[subRaw.plan_key] || {};
  return {
    id:                       subRaw.subscription_id,
    plan_key:                 subRaw.plan_key,
    plan_name:                subRaw.plan_name,
    plan_family:              planDef.plan_family || null,
    size_band:                planDef.size_band   || null,
    status:                   subRaw.status,
    started_at:               subRaw.started_at,
    current_period_end:       subRaw.current_period_end,
    price_brl_cents:          subRaw.monthly_price_cents,
    athletes_quota:           subRaw.athlete_limit,
    active_athletes_count:    (subRaw.athletes || {}).active || 0,
    suspended_athletes_count: (subRaw.athletes || {}).suspended_by_quota || 0,
    staff_seats_quota:        subRaw.staff_limit,
    ai_credits_quota:         subRaw.ai_credits_monthly,
    video_credits_quota:      subRaw.video_credits_monthly,
  };
};

/**
 * Lista TODAS as institutions com seu estado de subscription, em shape ergonômico.
 *
 * Faz 2 fetches em paralelo: /subscriptions (com counts) + /plans (pra enriquecer
 * family/size_band que NÃO vem na sub).
 *
 * @returns {Promise<{items: Array, plans: Array, totals: Object}>}
 *   items: [{institution_id, institution_name, institution_type,
 *           sub: {id, plan_key, plan_name, plan_family, size_band, status,
 *                 active_athletes_count, suspended_athletes_count,
 *                 athletes_quota, staff_seats_quota, ai_credits_quota,
 *                 price_brl_cents} | null,
 *           active_athletes_count_no_sub: number  // só se sub == null
 *          }]
 *   plans: lista de plan_definitions ativos (pra select do modal)
 *   totals: {active, replaced, cancelled, without_subscription}
 */
export const listSubscriptions = async () => {
  const [subsResp, plansResp] = await Promise.all([
    AZURE.get('/governance/comercial/subscriptions'),
    AZURE.get('/governance/comercial/plans?active_only=true'),
  ]);

  const subsPayload  = subsResp.data?.data || subsResp.data || {};
  const plansPayload = plansResp.data?.data || plansResp.data || {};

  const subsRaw   = subsPayload.subscriptions || [];
  const noSubRaw  = subsPayload.institutions_without_subscription || [];
  const totals    = subsPayload.totals || {};
  const plans     = plansPayload.plans || [];
  const plansMap  = buildPlansMap(plans);

  // Agrupa por institution_id: prioriza active > replaced > cancelled
  const rank = { active: 3, replaced: 2, cancelled: 1 };
  const byInst = {};
  subsRaw.forEach((s) => {
    const id = s.institution_id;
    const enriched = {
      institution_id:   id,
      institution_name: s.institution_name,
      institution_type: s.institution_type,
      sub: _enrichSub(s, plansMap),
    };
    const prev = byInst[id];
    if (!prev || (rank[enriched.sub.status] || 0) > (rank[prev.sub.status] || 0)) {
      byInst[id] = enriched;
    }
  });

  const items = Object.values(byInst);

  // Institutions sem sub
  noSubRaw.forEach((i) => {
    items.push({
      institution_id:               i.institution_id,
      institution_name:             i.institution_name,
      institution_type:             i.institution_type,
      sub:                          null,
      active_athletes_count_no_sub: i.athletes_active || 0,
    });
  });

  // Ordena: com sub ativa primeiro, depois com sub não-ativa, depois sem sub
  items.sort((a, b) => {
    const rk = (x) => {
      if (!x.sub) return 0;
      if (x.sub.status === 'active') return 3;
      if (x.sub.status === 'replaced') return 2;
      return 1;
    };
    return rk(b) - rk(a);
  });

  return { items, plans, totals };
};

/**
 * Dry-run de troca de plano. Calcula quantos atletas seriam suspensos
 * e retorna OS NOMES (safety brake).
 *
 * Normaliza shape do backend pra um campo plano consumido direto pelo modal.
 */
export const previewChange = async (institutionId, planKey) => {
  const { data } = await AZURE.get('/governance/comercial/subscriptions/preview-change', {
    params: { institution_id: institutionId, plan_key: planKey },
  });
  const payload = data?.data || data || {};
  const cur     = payload.current_subscription || {};
  const tgt     = payload.target_plan          || {};
  const impact  = payload.impact               || {};

  return {
    institution_id:           payload.institution_id,
    institution_name:         payload.institution_name,
    verdict_preview:          payload.verdict_preview,
    current_plan_key:         cur.plan_key  || null,
    current_plan_name:        cur.plan_name || null,
    current_athletes_quota:   cur.athlete_limit || null,
    target_plan_key:          tgt.plan_key,
    target_plan_name:         tgt.plan_name,
    target_athletes_quota:    tgt.athlete_limit_new,
    target_price_brl_cents:   tgt.monthly_price_cents,
    current_athletes_count:   impact.athletes_active_now || 0,
    would_suspend_count:      impact.would_suspend || 0,
    would_suspend_athletes:   impact.would_suspend_athletes || [],
  };
};

/**
 * Cria/troca subscription. Atletas excedentes são suspensos automaticamente
 * (suspension_reason='plan_quota_exceeded:<plan_key>') + alerta MEDIUM no gestor.
 */
export const createSubscription = async (body) => {
  const { data } = await AZURE.post('/governance/comercial/subscriptions', body);
  return data?.data || data || {};
};

/**
 * Cancela subscription (soft cancel — status='cancelled'). Atletas continuam
 * no club, gestor decide o que fazer depois.
 */
export const cancelSubscription = async (subId, body = {}) => {
  const { data } = await AZURE.post(`/governance/comercial/subscriptions/${subId}/cancel`, body);
  return data?.data || data || {};
};
